/**
 * 🏛️ The Aethelgard Bazaar — player-crafted marketplace.
 *
 * Canon: /app/memory/AETHELGARD_BAZAAR_CANON_FEB2026.md
 * Rule:  Only items a player forged can be listed here.
 *
 * Tabs: Browse · List an Item · My Listings · History
 * Stalls: Ironworks · Alembic · Cauldron · Curio · Raw Rack
 * Haggle: buyer spends a Persuasion Item to unlock a discount roll (60s offer).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const RARITY_BADGE = {
  common:    "bg-slate-700/40 border-slate-500 text-slate-200",
  uncommon:  "bg-emerald-900/40 border-emerald-700 text-emerald-200",
  rare:      "bg-blue-900/40 border-blue-700 text-blue-200",
  forged:    "bg-amber-900/40 border-amber-700 text-amber-200",
  epic:      "bg-fuchsia-900/40 border-fuchsia-700 text-fuchsia-200",
  legendary: "bg-purple-900/40 border-purple-700 text-purple-200",
};

const CURRENCY_ICON = {
  chits: "🪙",
  omni_dollars: "💎",
  bazaar_tokens: "🎟️",
};

function fmtNum(n) { return new Intl.NumberFormat().format(Number(n || 0)); }

function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!iso) return { seconds: 0, label: "" };
  const target = new Date(iso).getTime();
  const secs = Math.max(0, Math.floor((target - now) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return {
    seconds: secs,
    label: h ? `${h}h ${m}m` : m ? `${m}m ${s}s` : `${s}s`,
  };
}

function CurrencyChip({ currency, value, testid }) {
  const icon = CURRENCY_ICON[currency] || "🪙";
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full border border-purple-700/60 bg-purple-950/40 px-2 py-0.5 text-xs font-mono text-purple-100"
    >
      <span>{icon}</span>
      <span>{fmtNum(value)}</span>
    </span>
  );
}

function ListingCard({ listing, onHaggle, onBuy, onRetrieve, mine, buying, persuasionItems }) {
  const [showHaggle, setShowHaggle] = useState(false);
  const owned = persuasionItems?.filter((p) => p.owned > 0) || [];
  const activeHaggle = listing.active_haggle;
  const canBuy = !mine && listing.status === "active";
  const rarity = listing.item_meta?.rarity || "common";

  return (
    <div
      data-testid={`bazaar-listing-${listing.listing_id}`}
      className="group relative flex flex-col rounded-xl border border-purple-800/40 bg-gradient-to-b from-purple-950/40 to-slate-950/60 p-4 transition-all hover:border-purple-500/60 hover:shadow-[0_0_28px_-8px_rgba(168,85,247,0.6)]"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-3xl">{listing.item_meta?.icon || "📦"}</span>
          <div>
            <div className="font-semibold text-purple-50">{listing.item_meta?.name || listing.item_id}</div>
            <div className="text-[10px] uppercase tracking-wider text-purple-300/70">
              by @{listing.seller_handle || listing.seller_name || "phoenix"} · forged via {listing.crafted_via}
            </div>
          </div>
        </div>
        <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase ${RARITY_BADGE[rarity] || RARITY_BADGE.common}`}>
          {rarity}
        </span>
      </div>

      <div className="mt-auto flex items-center justify-between">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl">{CURRENCY_ICON[listing.currency] || "🪙"}</span>
          <span className="text-2xl font-bold text-purple-50">{fmtNum(listing.price)}</span>
        </div>
        {mine ? (
          <button
            data-testid={`bazaar-retrieve-${listing.listing_id}`}
            onClick={() => onRetrieve?.(listing)}
            className="rounded border border-amber-700/60 bg-amber-950/40 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-900/60"
          >
            🪝 Retrieve
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              data-testid={`bazaar-haggle-open-${listing.listing_id}`}
              disabled={!canBuy || owned.length === 0}
              onClick={() => setShowHaggle((v) => !v)}
              className="rounded border border-purple-700/60 bg-purple-950/40 px-2.5 py-1.5 text-xs font-semibold text-purple-100 hover:bg-purple-900/60 disabled:cursor-not-allowed disabled:opacity-40"
              title={owned.length === 0 ? "No Persuasion Items — buy at listed price" : "Try to haggle"}
            >
              🎭 Haggle
            </button>
            <button
              data-testid={`bazaar-buy-${listing.listing_id}`}
              disabled={!canBuy || buying}
              onClick={() => onBuy?.(listing)}
              className="rounded border border-emerald-700/60 bg-emerald-950/50 px-3 py-1.5 text-xs font-semibold text-emerald-100 hover:bg-emerald-900/60 disabled:opacity-40"
            >
              {activeHaggle && activeHaggle.buyer_user_id ? "Buy @ Offer" : "Buy"}
            </button>
          </div>
        )}
      </div>

      {showHaggle && !mine && (
        <div
          data-testid={`bazaar-haggle-picker-${listing.listing_id}`}
          className="mt-3 rounded-lg border border-purple-800/60 bg-slate-950/60 p-2"
        >
          <div className="mb-1 text-[11px] text-purple-200">Pick a Persuasion Item — it will be consumed:</div>
          <div className="flex flex-wrap gap-1.5">
            {owned.length === 0 && (
              <span className="text-xs italic text-slate-400">You have none. Buy at listed price.</span>
            )}
            {owned.map((p) => (
              <button
                key={p.key}
                data-testid={`bazaar-haggle-use-${p.key}-${listing.listing_id}`}
                onClick={async () => {
                  setShowHaggle(false);
                  await onHaggle?.(listing, p.key);
                }}
                className="flex items-center gap-1 rounded border border-purple-700/60 bg-purple-950/40 px-2 py-1 text-xs text-purple-50 hover:bg-purple-900/60"
              >
                <span>{p.icon}</span>
                <span>{p.name}</span>
                <span className="text-purple-300/70">×{p.owned}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActiveHaggleOffer({ offer, onBuy, onDismiss }) {
  const { seconds, label } = useCountdown(offer?.expires_at);
  useEffect(() => {
    if (offer && seconds === 0) onDismiss?.();
  }, [seconds, offer, onDismiss]);
  if (!offer) return null;
  return (
    <div
      data-testid="bazaar-active-haggle-offer"
      className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/50 bg-amber-950/40 p-3 text-amber-50"
    >
      <div>
        <div className="text-sm font-semibold">🎭 Offer locked — {offer.discount_pct}% off</div>
        <div className="text-xs text-amber-200/80">
          {offer.item_name} · {CURRENCY_ICON[offer.currency]} {fmtNum(offer.listed_price)}
          {" → "}
          <span className="font-bold text-emerald-200">{fmtNum(offer.offer_price)}</span>
          {"  · expires in "}
          <span className="font-mono">{label}</span>
        </div>
      </div>
      <button
        data-testid="bazaar-buy-haggle-offer"
        onClick={() => onBuy?.(offer)}
        className="rounded border border-emerald-500/70 bg-emerald-800/60 px-4 py-2 text-sm font-bold text-emerald-50 hover:bg-emerald-700/70"
      >
        Confirm Buy @ {fmtNum(offer.offer_price)}
      </button>
    </div>
  );
}

// ─── 🎣 Fishing Rod Tier System — Coming Soon locked preview ──────────
const ROD_RARITY_TONE = {
  common:    { ring: "border-slate-500/60",    tint: "from-slate-900/60",     text: "text-slate-200" },
  uncommon:  { ring: "border-emerald-500/60",  tint: "from-emerald-950/40",   text: "text-emerald-200" },
  rare:      { ring: "border-sky-500/60",      tint: "from-sky-950/40",       text: "text-sky-200" },
  epic:      { ring: "border-fuchsia-500/60",  tint: "from-fuchsia-950/40",   text: "text-fuchsia-200" },
  legendary: { ring: "border-amber-500/70",    tint: "from-amber-950/50",     text: "text-amber-200" },
};

function FishingRodComingSoon({ rods }) {
  return (
    <div className="mt-10 pt-6 border-t border-cyan-500/25" data-testid="fishing-rods-coming-soon">
      <div className="mb-4 flex flex-wrap items-baseline gap-3">
        <span className="text-xs font-black uppercase tracking-[0.14em] text-cyan-300">
          🎣 Fishing Rod Marketplace
        </span>
        <span className="rounded-full border border-cyan-400/60 bg-black/70 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-cyan-300 shadow-[0_0_12px_rgba(34,211,238,0.25)]">
          🔒 Coming Soon
        </span>
        <span className="text-[11px] italic text-cyan-200/70">
          Player-forged rods will drop into these stalls when the fishing minigame goes live.
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rods.map((rod) => {
          const tone = ROD_RARITY_TONE[rod.rarity] || ROD_RARITY_TONE.common;
          return (
            <div
              key={rod.id}
              className={`relative rounded-2xl border ${tone.ring} bg-gradient-to-br ${tone.tint} via-black/60 to-black/70 p-4 flex flex-col gap-2 opacity-95 hover:opacity-100 transition`}
              data-testid={`fishing-rod-card-${rod.id}`}
            >
              <div className="absolute top-3 right-3 rounded-full border border-cyan-400/50 bg-black/70 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-cyan-300">
                🔒 Preview
              </div>
              <div className="flex items-start gap-3">
                <div className="text-4xl drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]">{rod.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300/80">
                    Tier {rod.tier} · {rod.rarity}
                  </div>
                  <div className={`text-base font-black leading-tight ${tone.text}`}>
                    {rod.name}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1.5 text-[10px] uppercase tracking-wider">
                <div className="rounded bg-black/50 border border-white/10 px-2 py-1 text-center">
                  <div className="text-neutral-400">Bite Rate</div>
                  <div className="text-cyan-200 font-bold">
                    {rod.bite_rate_bonus_pct > 0 ? `+${rod.bite_rate_bonus_pct}%` : "Base"}
                  </div>
                </div>
                <div className="rounded bg-black/50 border border-white/10 px-2 py-1 text-center">
                  <div className="text-neutral-400">Tension</div>
                  <div className="text-cyan-200 font-bold">{rod.line_tension_max_lbs} lbs</div>
                </div>
                <div className="rounded bg-black/50 border border-white/10 px-2 py-1 text-center">
                  <div className="text-neutral-400">Durability</div>
                  <div className="text-cyan-200 font-bold">{rod.durability_max}</div>
                </div>
              </div>
              {rod.unique_trait && (
                <div className="rounded border-l-2 border-cyan-500/40 bg-cyan-950/20 px-2 py-1.5">
                  <div className="text-[9px] uppercase tracking-widest text-cyan-300 font-bold">
                    {rod.unique_trait.name}
                  </div>
                  <div className="text-[11px] text-cyan-100/85 leading-snug">
                    {rod.unique_trait.description}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function AethelgardBazaar() {
  const [state, setState] = useState(null);
  const [listings, setListings] = useState([]);
  const [my, setMy] = useState({ active: [], sold: [] });
  const [listable, setListable] = useState([]);
  const [tab, setTab] = useState("browse");
  const [stall, setStall] = useState("all");
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [activeOffer, setActiveOffer] = useState(null); // haggle offer waiting for confirm
  const [listForm, setListForm] = useState({ item_id: "", stall_id: "", currency: "chits", price: 100 });
  const [history, setHistory] = useState({ bought: [], sold: [] });
  const [fishingRods, setFishingRods] = useState([]); // 🎣 locked preview cards

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, m, li, h] = await Promise.all([
        axios.get(`${API}/bazaar/state`).then((r) => r.data),
        axios.get(`${API}/bazaar/listings?limit=200`).then((r) => r.data.listings || []),
        axios.get(`${API}/bazaar/my-listings`).then((r) => r.data),
        axios.get(`${API}/bazaar/listable`).then((r) => r.data.items || []),
        axios.get(`${API}/bazaar/history`).then((r) => r.data),
      ]);
      setState(s);
      setListings(l);
      setMy(m);
      setListable(li);
      setHistory(h);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bazaar unavailable");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // 🎣 Load fishing rod coming-soon preview (locked, non-blocking)
  useEffect(() => {
    axios.get(`${API}/fishing/rods/catalog`)
      .then((r) => setFishingRods(r.data?.rods || []))
      .catch(() => setFishingRods([]));
  }, []);

  const filtered = useMemo(() => {
    if (stall === "all") return listings;
    return listings.filter((l) => l.stall_id === stall);
  }, [listings, stall]);

  const persuasionItems = state?.persuasion_items || [];

  const doHaggle = async (listing, persuasion_item) => {
    try {
      const r = await axios.post(`${API}/bazaar/haggle`, {
        listing_id: listing.listing_id,
        persuasion_item,
      });
      const offer = { ...r.data, item_name: listing.item_meta?.name };
      setActiveOffer(offer);
      toast.success(offer.message);
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Haggle failed");
    }
  };

  const doBuy = async (listing) => {
    setBuying(true);
    try {
      const r = await axios.post(`${API}/bazaar/purchase`, {
        listing_id: listing.listing_id,
      });
      const d = r.data || {};
      toast.success(
        `Bought ${d.icon || ""} ${d.item_name} for ${CURRENCY_ICON[d.currency] || ""} ${fmtNum(d.price_paid)}` +
        (d.haggle_pct ? ` (${d.haggle_pct}% off)` : "")
      );
      setActiveOffer(null);
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed");
    } finally {
      setBuying(false);
    }
  };

  const doRetrieve = async (listing) => {
    if (!window.confirm(`Pull ${listing.item_meta?.name} back to your inventory?`)) return;
    try {
      await axios.post(`${API}/bazaar/retrieve`, { listing_id: listing.listing_id });
      toast.success(`Retrieved ${listing.item_meta?.name}`);
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retrieve failed");
    }
  };

  const doList = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bazaar/list`, {
        item_id: listForm.item_id,
        stall_id: listForm.stall_id,
        currency: listForm.currency,
        price: Number(listForm.price),
      });
      toast.success("Listed at the Bazaar 🏛️");
      setListForm({ item_id: "", stall_id: "", currency: "chits", price: 100 });
      setTab("mine");
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Listing failed");
    }
  };

  const restockCountdown = useCountdown(state?.next_restock_at);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-950/30 to-black text-purple-50">
      <Header />
      <div className="mx-auto max-w-6xl px-4 py-8" data-testid="aethelgard-bazaar-page">
        {/* Title */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-4xl font-bold tracking-tight text-purple-100 sm:text-5xl">
              🏛️ The Aethelgard Bazaar
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-purple-300/80">
              A player-forged market beside the Auction House. Only wares crafted by your own hands land here.
              Wander the stalls, spend Persuasion Items to haggle, or list your own creations for another Phoenix to find.
            </p>
            {/* 🎟️ Owner canon Oct 2026 (Fix #29): name the currency and
                tell new players how to earn tickets. Previously the Bazaar
                priced everything in 🎟️ without ever defining what they are. */}
            <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-950/30 px-2.5 py-1 text-[11px] text-amber-200" data-testid="bazaar-currency-pointer">
              🎟️ <b>Aethelgard Bazaar Tickets</b> — earn them from Phantom Nest clears (50 at Nightmare · 200 at Cosmic) or buy bundles at the <a href="/nest" className="underline hover:text-amber-100">Nest Shop</a> (500 PT → 5 · 1,200 PT → 13 · 2,500 PT → 26).
            </div>
          </div>
          <div className="flex flex-col items-end text-right text-xs text-purple-300/80">
            <div>Featured rotation in <span className="font-mono text-purple-100">{restockCountdown.label}</span></div>
            <a
              href="/alembic"
              data-testid="bazaar-to-alembic-link"
              className="mt-1 rounded-full border border-emerald-500/40 bg-emerald-950/40 px-3 py-1 text-[11px] font-semibold text-emerald-100 hover:bg-emerald-900/50"
            >
              🧪 Brew at the Alembic →
            </a>
            <div className="mt-1 flex gap-2">
              <CurrencyChip currency="chits" value={state?.wallet?.chits} testid="bazaar-wallet-chits" />
              <CurrencyChip currency="omni_dollars" value={state?.wallet?.omni_dollars} testid="bazaar-wallet-od" />
              <CurrencyChip currency="bazaar_tokens" value={state?.wallet?.bazaar_tokens} testid="bazaar-wallet-tokens" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-2 border-b border-purple-800/40 pb-1">
          {[
            ["browse", "Browse"],
            ["list", "List an Item"],
            ["mine", "My Listings"],
            ["history", "History"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`bazaar-tab-${k}`}
              onClick={() => setTab(k)}
              className={`rounded-t-md px-4 py-2 text-sm font-semibold transition-colors ${
                tab === k
                  ? "border border-b-transparent border-purple-600 bg-purple-900/40 text-purple-50"
                  : "text-purple-300/80 hover:text-purple-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeOffer && (
          <ActiveHaggleOffer
            offer={activeOffer}
            onBuy={() => doBuy({ listing_id: activeOffer.listing_id })}
            onDismiss={() => setActiveOffer(null)}
          />
        )}

        {/* BROWSE */}
        {tab === "browse" && (
          <div>
            <div className="mb-4 flex flex-wrap gap-2" data-testid="bazaar-stall-filter">
              <button
                data-testid="bazaar-stall-all"
                onClick={() => setStall("all")}
                className={`rounded-full border px-3 py-1 text-xs ${
                  stall === "all"
                    ? "border-purple-400 bg-purple-800/60 text-white"
                    : "border-purple-800/40 bg-purple-950/30 text-purple-200 hover:bg-purple-900/40"
                }`}
              >
                All Stalls
              </button>
              {state?.stalls?.map((s) => (
                <button
                  key={s.stall_id}
                  data-testid={`bazaar-stall-${s.stall_id}`}
                  onClick={() => setStall(s.stall_id)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    stall === s.stall_id
                      ? "border-purple-400 bg-purple-800/60 text-white"
                      : "border-purple-800/40 bg-purple-950/30 text-purple-200 hover:bg-purple-900/40"
                  }`}
                >
                  {s.icon} {s.name}
                  <span className="ml-1 rounded bg-black/40 px-1 py-0.5 text-[10px] font-mono">{s.listings_total}</span>
                </button>
              ))}
            </div>

            {state?.stalls && stall !== "all" && (
              <div className="mb-4 rounded-lg border border-purple-900/40 bg-purple-950/30 p-3 text-sm italic text-purple-200/80">
                {state.stalls.find((s) => s.stall_id === stall)?.flavor}
              </div>
            )}

            {loading ? (
              <div className="py-16 text-center text-purple-300">Loading the stalls…</div>
            ) : filtered.length === 0 ? (
              <div className="rounded-xl border border-purple-800/40 bg-purple-950/30 p-10 text-center">
                <div className="mb-2 text-4xl">🕯️</div>
                <div className="text-lg font-semibold text-purple-100">Quiet in the aisles.</div>
                <div className="mt-1 text-sm text-purple-300/80">
                  No hands have crafted for this stall yet. Forge something at the Blacksmith and be the first.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="bazaar-listings-grid">
                {filtered.map((l) => (
                  <ListingCard
                    key={l.listing_id}
                    listing={l}
                    onHaggle={doHaggle}
                    onBuy={doBuy}
                    persuasionItems={persuasionItems}
                    buying={buying}
                  />
                ))}
              </div>
            )}

            {/* 🎣 Fishing Rod Tier System — Coming Soon preview */}
            {fishingRods.length > 0 && (
              <FishingRodComingSoon rods={fishingRods} />
            )}
          </div>
        )}

        {/* LIST */}
        {tab === "list" && (
          <div className="max-w-2xl">
            <div className="mb-3 rounded-lg border border-amber-800/40 bg-amber-950/30 p-3 text-sm text-amber-100">
              ⚒️ Only items <b>you crafted</b> can be listed. Forge at the Blacksmith, craft in the Kitchen, or mint at the Workshop first.
            </div>

            {listable.length === 0 ? (
              <div className="rounded-xl border border-purple-800/40 bg-purple-950/30 p-8 text-center text-purple-200">
                <div className="mb-2 text-3xl">🔨</div>
                Nothing crafted yet. Head to the <a href="/blacksmith" className="underline">Blacksmith</a> to forge your first wares.
              </div>
            ) : (
              <form onSubmit={doList} className="space-y-4" data-testid="bazaar-list-form">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-purple-300">Crafted Item</span>
                  <select
                    data-testid="bazaar-list-item-select"
                    required
                    value={listForm.item_id}
                    onChange={(e) => {
                      const it = listable.find((x) => x.item_id === e.target.value);
                      setListForm((f) => ({
                        ...f,
                        item_id: e.target.value,
                        stall_id: it?.suggested_stall || f.stall_id,
                      }));
                    }}
                    className="w-full rounded border border-purple-800/60 bg-slate-950/60 p-2 text-purple-50"
                  >
                    <option value="">— pick one —</option>
                    {listable.map((it) => (
                      <option key={it.item_id} value={it.item_id}>
                        {it.icon} {it.name} · ×{it.listable ?? it.owned} · {it.rarity}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-purple-300">Stall</span>
                  <select
                    data-testid="bazaar-list-stall-select"
                    required
                    value={listForm.stall_id}
                    onChange={(e) => setListForm((f) => ({ ...f, stall_id: e.target.value }))}
                    className="w-full rounded border border-purple-800/60 bg-slate-950/60 p-2 text-purple-50"
                  >
                    <option value="">— pick a stall —</option>
                    {state?.stalls?.map((s) => (
                      <option key={s.stall_id} value={s.stall_id}>
                        {s.icon} {s.name}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-purple-300">Currency</span>
                    <select
                      data-testid="bazaar-list-currency-select"
                      value={listForm.currency}
                      onChange={(e) => setListForm((f) => ({ ...f, currency: e.target.value }))}
                      className="w-full rounded border border-purple-800/60 bg-slate-950/60 p-2 text-purple-50"
                    >
                      <option value="chits">🪙 Chits</option>
                      <option value="omni_dollars">💎 Omni Dollars</option>
                      <option value="bazaar_tokens">🎟️ Bazaar Tokens</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-purple-300">Price</span>
                    <input
                      data-testid="bazaar-list-price-input"
                      type="number"
                      min={1}
                      required
                      value={listForm.price}
                      onChange={(e) => setListForm((f) => ({ ...f, price: e.target.value }))}
                      className="w-full rounded border border-purple-800/60 bg-slate-950/60 p-2 text-purple-50"
                    />
                  </label>
                </div>

                <div className="rounded-lg border border-purple-900/40 bg-purple-950/30 p-3 text-xs text-purple-200/80">
                  House cut: <b>{Math.round((state?.house_cut_pct || 0) * 100)}%</b> burned on sale.
                  Rebate to you as 🎟️ Bazaar Tokens: <b>{Math.round((state?.rebate_pct || 0) * 100)}%</b> of sale price.
                </div>

                <button
                  type="submit"
                  data-testid="bazaar-list-submit"
                  className="w-full rounded-lg border border-purple-600/70 bg-purple-800/60 py-2.5 font-semibold text-purple-50 hover:bg-purple-700/70"
                >
                  🏛️ List at the Bazaar
                </button>
              </form>
            )}
          </div>
        )}

        {/* MINE */}
        {tab === "mine" && (
          <div className="space-y-6">
            <div>
              <h2 className="mb-3 text-lg font-bold text-purple-100">🏷️ Active Listings ({my.active.length})</h2>
              {my.active.length === 0 ? (
                <div className="rounded-xl border border-purple-800/40 bg-purple-950/30 p-6 text-center text-sm text-purple-300">
                  Nothing on the shelves. Craft, then list.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="bazaar-my-active-grid">
                  {my.active.map((l) => (
                    <ListingCard key={l.listing_id} listing={l} mine onRetrieve={doRetrieve} persuasionItems={persuasionItems} />
                  ))}
                </div>
              )}
            </div>
            <div>
              <h2 className="mb-3 text-lg font-bold text-purple-100">✅ Recently Sold ({my.sold.length})</h2>
              {my.sold.length === 0 ? (
                <div className="text-sm italic text-purple-300/70">No sales yet.</div>
              ) : (
                <ul className="space-y-2 text-sm" data-testid="bazaar-my-sold-list">
                  {my.sold.map((l) => (
                    <li key={l.listing_id} className="flex items-center justify-between rounded border border-emerald-900/40 bg-emerald-950/20 px-3 py-2">
                      <span>{l.item_meta?.icon} {l.item_meta?.name} → @{l.buyer_user_id?.slice(-4)}</span>
                      <span className="font-mono">{CURRENCY_ICON[l.currency]} {fmtNum(l.final_price || l.price)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* HISTORY */}
        {tab === "history" && (
          <div className="grid gap-4 md:grid-cols-2" data-testid="bazaar-history">
            <div>
              <h2 className="mb-2 text-lg font-bold text-purple-100">🛍️ Bought</h2>
              {history.bought.length === 0 ? (
                <div className="text-sm italic text-purple-300/70">No purchases yet.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {history.bought.map((p) => (
                    <li key={p.purchase_id} className="rounded border border-purple-800/40 bg-purple-950/30 px-3 py-2">
                      <div className="flex justify-between">
                        <span>{p.item_name}</span>
                        <span className="font-mono">{CURRENCY_ICON[p.currency]} {fmtNum(p.price_paid)}</span>
                      </div>
                      {p.haggle_pct != null && (
                        <div className="text-[11px] text-emerald-300">
                          {p.persuasion_item} · {p.haggle_pct}% off
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h2 className="mb-2 text-lg font-bold text-purple-100">💰 Sold</h2>
              {history.sold.length === 0 ? (
                <div className="text-sm italic text-purple-300/70">No sales yet.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {history.sold.map((p) => (
                    <li key={p.purchase_id} className="rounded border border-emerald-900/40 bg-emerald-950/20 px-3 py-2">
                      <div className="flex justify-between">
                        <span>{p.item_name}</span>
                        <span className="font-mono">
                          +{CURRENCY_ICON[p.currency]} {fmtNum(p.seller_take)} · +🎟️ {fmtNum(p.rebate_tokens)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
